import { useShallow } from "zustand/shallow";
import {
  createApiClient,
  createCustomerApiClient,
  RefreshManager,
  CUSTOMER_REFRESH_PATH_SUFFIX,
  type CustomerApiClient,
} from "@njbsoft/api-client";
import { validateRedirect } from "@njbsoft/utils";
import { useCustomerStore } from "@/store";
import { queryClient } from "@/lib/query-client";
import { readPersistedSessionTokens } from "@/lib/persisted-session";
import { acceptInviteLandingAt } from "@/lib/account-invite-links";

const API_URL = import.meta.env.VITE_API_URL;

// The customer stack lives at /api/v1/customer. CUSTOMER_REFRESH_PATH_SUFFIX is
// the "/customer" segment the api-client package owns; appending it here keeps
// the suffix a single cross-package contract instead of a string literal that
// could drift. CUSTOMER_BASE_URL is the base every customer-spec op (auth, data,
// invite) rides.
const CUSTOMER_BASE_URL = `${API_URL}${CUSTOMER_REFRESH_PATH_SUFFIX}`;

// CLIENT ISOLATION (the apps/company posture, restated for the customer stack):
//
//   customerApiClient  — the customer data plane AND the customer-spec auth +
//                        accept-invite ops. baseUrl = ${API_URL}/customer. Every
//                        workspace read/write, login, logout, forgot-password,
//                        and mfa op rides this client. The customer stack forks
//                        its own /auth surface (unlike the company app), so the
//                        auth ops live on the customer spec and ride this client.
//
//   authApiClient      — the main-spec-typed client at the BARE ${API_URL}.
//                        Legal for EXACTLY ONE op: the genuinely-main-spec
//                        /auth/reset-password confirm endpoint. The customer
//                        forgot-password flow delegates to the shared
//                        reset-token table; its email links to
//                        /portal/{slug}/reset-password on the customer app, and
//                        that reset confirms through the main
//                        /auth/reset-password op (the customer stack does NOT
//                        fork a reset-password confirm op). Customer DATA never
//                        flows through this client. The deliberate name makes
//                        `grep -rn "authApiClient\." src/` an audit, not a hunt.

// The single sign-out teardown path for the whole portal. Every site that ends
// a session — the sign-out button, the access-denied screen, and the
// auth-failure callbacks below — funnels through here so the clear sequence
// (active context → auth → query cache → hard nav) lives in exactly one place.
// `revoke: true` additionally fires a best-effort server-side logout first (the
// user pressed sign-out, so we ask the server to revoke the refresh token); the
// auth-failure paths pass `revoke: false` because the session is already dead.
// The hard navigation is intentional: a full page reload gives a clean slate
// with no risk of stale state leaking. It lands on /login by default, or on the
// in-app path a caller needs the visitor returned to.
export function signOut({
  revoke,
  redirectTo,
}: {
  revoke: boolean;
  redirectTo?: string | null;
}): void {
  if (revoke) {
    const { refreshToken } = useCustomerStore.getState();
    if (refreshToken) {
      customerApiClient
        .POST("/auth/logout", { body: { refresh_token: refreshToken } })
        .catch(() => {
          // Sign out regardless — server revocation is best effort.
        });
    }
  }
  useCustomerStore.getState().clearActiveContext();
  useCustomerStore.getState().clearAuth();
  queryClient.clear();
  // Validated even though every caller builds this internally: a future caller
  // handed user input would otherwise turn the sign-out into an open redirect.
  window.location.href = validateRedirect(redirectTo) ?? "/login";
}

/**
 * The auth-failure teardown: the session is already dead, so nothing is
 * revoked. On the invite landing the token in the address bar is the visit's
 * whole payload, so the teardown returns there rather than to a bare /login;
 * the page re-renders cold, signed out, with sign-in on offer and the token
 * intact. Everywhere else it is the /login default.
 */
export function signOutExpiredSession(): void {
  signOut({
    revoke: false,
    redirectTo: acceptInviteLandingAt(window.location),
  });
}

// The shared RefreshManager POSTs to `${baseUrl}/auth/refresh`, so baseUrl MUST
// be the customer base — a njbsoft-customer refresh token is redeemable ONLY at
// /api/v1/customer/auth/refresh (the audience discriminator rejects it on the
// internal endpoint). Wiring it at the bare ${API_URL} would silently log out
// every customer on the first refresh. The 200 body
// {access_token, refresh_token, expires_at} is the contract onTokenRefreshed
// consumes. The customer login result carries NO user object, so the stored
// user stays whatever GET /me last hydrated across a refresh.
//
// The last three options are the cross-tab layer: the manager takes an
// exclusive Web Lock around every refresh and, inside it, adopts a sibling's
// rotation from the persisted store when that pair is stamped LATER than the
// one captured here — because a tab replaying a token its sibling rotated more
// than the server's grace window ago reads as token theft and revokes the whole
// rotation family.
export const refreshManager = new RefreshManager({
  baseUrl: CUSTOMER_BASE_URL,
  getRefreshToken: () => useCustomerStore.getState().refreshToken,
  onTokenRefreshed: (accessToken, refreshToken, expiresAt) => {
    const state = useCustomerStore.getState();
    state.setAuth(accessToken, state.user, refreshToken);
    state.setExpiresAt(new Date(expiresAt).getTime());
  },
  onRefreshFailed: signOutExpiredSession,
  getPersistedTokens: readPersistedSessionTokens,
  adoptTokens: (tokens) => {
    useCustomerStore.getState().adoptTokens(tokens);
  },
  getTokenStamp: () => useCustomerStore.getState().rotatedAt,
});

// Main-spec client at the BARE base. Reserved for the single main-spec op the
// customer stack does not fork: /auth/reset-password, the shared reset-token
// confirm endpoint behind the customer app's /portal/{slug}/reset-password page
// the forgot-password email links to. NOT a data client.
export const authApiClient = createApiClient({
  baseUrl: API_URL,
  getToken: () => useCustomerStore.getState().token,
  refreshManager,
  onUnauthorized: signOutExpiredSession,
});

// The customer data plane: every workspace hook + the customer-spec auth/invite
// ops go through this client.
export const customerApiClient: CustomerApiClient = createCustomerApiClient({
  baseUrl: CUSTOMER_BASE_URL,
  getToken: () => useCustomerStore.getState().token,
  refreshManager,
  onUnauthorized: signOutExpiredSession,
});

// Auth context for the router.
export function useAuth() {
  return useCustomerStore(
    useShallow((s) => ({
      token: s.token,
      refreshToken: s.refreshToken,
      expiresAt: s.expiresAt,
      user: s.user,
      isAuthenticated: s.isAuthenticated,
      setAuth: s.setAuth,
      setRefreshToken: s.setRefreshToken,
      setExpiresAt: s.setExpiresAt,
      rotatedAt: s.rotatedAt,
      adoptTokens: s.adoptTokens,
      setEmailVerified: s.setEmailVerified,
      clearAuth: s.clearAuth,
      mfaSessionToken: s.mfaSessionToken,
      mfaPending: s.mfaPending,
      mfaMethods: s.mfaMethods,
      setMfaChallenge: s.setMfaChallenge,
      clearMfaChallenge: s.clearMfaChallenge,
    })),
  );
}
